import { classNames } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { forwardRef, useEffect } from 'react'
import DayPicker from './DayPicker'
import moment from './moment'
import MonthPicker from './MonthPicker'
import { FORMAT, useStartOfDay } from './utils'
import YearPicker from './YearPicker'

/**
 * Create a React Date Picker Dropdown component
 *
 * @param {import('./interfaces').DatePickerDropdownProps} props
 * @param {import('react').Ref<Element>} ref
 * @returns {JSX.Element}
 */
function datePickerDropDown (props, ref) {
  const {
    minDate,
    maxDate,
    date,
    displayedDate,
    currentPicker,
    onDaySelected,
    onMonthSelected,
    onYearSelected,
    onYearClick,
    onMonthClick,
    disableDayFn,
    onFocusChanged,
    id
  } = props
  const currentDay = useStartOfDay()

  useEffect(() => {
    if (onFocusChanged) {
      const focusedDay =
        displayedDate || (date === undefined ? currentDay : date)
      switch (currentPicker) {
        case 'day':
          onFocusChanged(
            `${id}-${moment(focusedDay).format(FORMAT)}`,
            focusedDay
          )
          break
        case 'month':
          onFocusChanged(
            `${id}-${moment.localeData().monthsShort()[focusedDay.getMonth()]}`, focusedDay
          )
          break
        case 'year':
          onFocusChanged(`${id}-${focusedDay.getFullYear()}`, focusedDay)
          break
      }
    }
  }, [currentPicker, onFocusChanged])

  return (
    <div
      className={classNames('zb-date-picker-dropdown', {
        'zb-date-picker-dropdown-is-shown': currentPicker
      })}
      ref={ref}
      id={id}
      tabIndex={currentPicker && 0}
      role='dialog'
      aria-modal='true'
      aria-label='Choose Date'
    >
      {renderMonthPicker()}
      {renderYearPicker()}
      {renderDayPicker()}
    </div>
  )

  function renderDayPicker () {
    const dayCalendarShown = currentPicker === 'day'
    return (
      <div
        className={classNames('zb-day-calendar-container', {
          'zb-day-calendar-container-is-shown': dayCalendarShown
        })}
      >
        {dayCalendarShown && (
          <DayPicker
            minDate={minDate}
            maxDate={maxDate}
            disableDayFn={disableDayFn}
            onDaySelected={onDaySelected}
            onMonthClick={onMonthClick}
            onYearClick={onYearClick}
            date={date}
            displayedDate={displayedDate}
            onFocusChanged={onFocusChanged}
            idPrefix={id}
            withKeyboardNavigation
          />
        )}
      </div>
    )
  }

  function renderMonthPicker () {
    const monthCalendarShown = currentPicker === 'month'
    return (
      <div
        className={classNames('zb-month-picker-container', {
          'zb-month-picker-container-is-shown': monthCalendarShown
        })}
      >
        {monthCalendarShown ? (
          <MonthPicker
            defaultYear={displayedDate.getFullYear()}
            isMonthSelected={(m, y) =>
              date && m === date.getMonth() && y === date.getFullYear()
            }
            isMonthDisabled={(m, y) => {
              const lastDayOfPrevMonth = new Date(y, m, -1)
              const firstDayOfNextMont = new Date(y, m + 1)

              return (
                (minDate && firstDayOfNextMont < minDate) ||
                (maxDate && lastDayOfPrevMonth > maxDate)
              )
            }}
            isPrevYearDisabled={(y) => {
              const lastMonthOfTheYear = new Date(y - 1, 11)

              return minDate ? lastMonthOfTheYear < minDate : false
            }}
            isNextYearDisabled={(y) => {
              const firstMonthOfTheYear = new Date(y + 1, 0)

              return maxDate ? firstMonthOfTheYear > maxDate : false
            }}
            displayedDate={displayedDate}
            onMonthSelected={onMonthSelected}
            onFocusChanged={onFocusChanged}
            idPrefix={id}
            withKeyboardNavigation
          />
        ) : null}
      </div>
    )
  }

  function renderYearPicker () {
    const yearCalendarShown = currentPicker === 'year'
    return (
      <div
        className={classNames(
          'zb-month-picker-container zb-year-picker-container',
          {
            'zb-month-picker-container-is-shown zb-year-picker-container-is-shown':
              yearCalendarShown
          }
        )}
      >
        {yearCalendarShown ? (
          <YearPicker
            defaultYear={displayedDate.getFullYear()}
            isYearSelected={(y) => date && y === date.getFullYear()}
            isYearDisabled={(y) =>
              (minDate && y < minDate.getFullYear()) ||
              (maxDate && y > maxDate.getFullYear())
            }
            isPrevYearDisabled={(y) => {
              const lastMonthOfTheYear = new Date(y - 1, 11)

              return minDate ? lastMonthOfTheYear < minDate : false
            }}
            isNextYearDisabled={(y) => {
              const firstMonthOfTheYear = new Date(y + 1, 0)

              return maxDate ? firstMonthOfTheYear > maxDate : false
            }}
            onYearSelected={onYearSelected}
            onFocusChanged={onFocusChanged}
            idPrefix={id}
            withKeyboardNavigation
          />
        ) : null}
      </div>
    )
  }
}

const dateType = PropTypes.instanceOf(Date)

const DatePickerDropDown = forwardRef(datePickerDropDown)
DatePickerDropDown.displayName = 'DatePickerDropdown'
DatePickerDropDown.propTypes = {
  id: PropTypes.string,
  currentPicker: PropTypes.string,
  minDate: dateType,
  maxDate: dateType,
  date: dateType,
  displayedDate: dateType,
  onMonthSelected: PropTypes.func,
  onYearSelected: PropTypes.func,
  onDaySelected: PropTypes.func,
  onYearClick: PropTypes.func,
  onMonthClick: PropTypes.func,
  onFocusChanged: PropTypes.func
}

export default DatePickerDropDown
